import { SphereButton } from "@components/common/sphere-button";
import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import {
  AutoCompleteMessage,
  FaroTextFieldMessage,
} from "@components/common/faro-text-field/faro-text-field-message";
import { LabelWithHelp } from "@components/common/label-with-help";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import {
  AutoCompleteMemberOption,
  AutoCompleteTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-types";
import {
  createMemberOption,
  createTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useCompanyMembers } from "@hooks/use-company-members";
import { Stack } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { EmailUtils } from "@stellar/utils";
import { addGroup } from "@store/groups/groups-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { useMemo, useState } from "react";
import { Dialog } from "@flat-ui";
import { STACK_ERROR_SX } from "@components/common/dialog/faro-dialog";

interface Props {
  /** List of all the available groups in the company */
  groups: FaroCoreApiTypes.GroupSummaryResponse[];
}

/**
 * Renders the create group button and the dialog to be used to create new groups.
 * This component does not take care of the authorization of the user to create groups.
 */
export function CreateGroup({ groups }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();

  const { companyId } = useAppParams();
  const companyMembers = useCompanyMembers();

  const currentUser = useAppSelector(currentUserSelector);
  const teams = useAppSelector(teamsSelector);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** Stores the name for the group to be created */
  const [groupName, setGroupName] = useState<string>("");
  /** Stores the ids of the users to be assigned as group managers */
  const [selectedGroupManagerIds, setSelectedGroupManagerIds] = useState<
    string[]
  >([]);

  /** Stores whether the create button should be disabled */
  const isCreateDisabled: boolean = useMemo(() => {
    // The first page is never disabled because the fields are validated
    return !groupName || !selectedGroupManagerIds.length;
  }, [groupName, selectedGroupManagerIds]);

  /** Stores the names of the groups the user has access to. */
  const groupNames = groups.map((group) => group.name);

  /** Stores the members of the company in the format required by the autocomplete */
  const memberOptions: AutoCompleteMemberOption[] = useMemo(() => {
    return companyMembers.map((member) => {
      /**
       * If the user is a group manager, it should be added to the group by default
       * and it should not be removed, otherwise the creator of the group would not have access to it.
       */
      const isPreselected =
        currentUser?.role === "COMPANY_MANAGER" &&
        currentUser?.identity === member.identity;

      return createMemberOption({
        member,
        isPreselected,
      });
    });
  }, [companyMembers, currentUser]);

  /** Reforming team data for stellar autocomplete component */
  const teamOptions: AutoCompleteTeamOption[] = useMemo(
    () => teams.map((team) => createTeamOption({ team })),
    [teams]
  );

  /** Stores the ids of the members of the company */
  const memberIds = useMemo(() => {
    return companyMembers.map(
      (member) => member.id?.toLowerCase() ?? member.email.toLowerCase()
    );
  }, [companyMembers]);

  /** Stores the message to be displayed in the autocomplete */
  const groupManagersMessage: AutoCompleteMessage | undefined = useMemo(() => {
    if (
      selectedGroupManagerIds.some(
        (member) => !memberIds.includes(member.toLowerCase())
      )
    ) {
      return {
        type: "info",
        helperText:
          "Inviting members by email will also invite them to the workspace",
      };
    }
    return undefined;
  }, [selectedGroupManagerIds, memberIds]);

  /** Resets the dialog content, and opens the dialog. */
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onOpenDialog(): Promise<void> {
    setGroupName("");
    // Add all the preselected members as group managers by default.
    const groupManagerIds: string[] = memberOptions
      .filter((member) => member.isPreselected)
      .map((member) => member.value);
    setSelectedGroupManagerIds(groupManagerIds);
    setIsDialogOpen(true);
  }

  /** Triggered when the create button is clicked, creates the group and closes the dialog */
  async function handleConfirm(): Promise<void> {
    if (!companyId) {
      throw new Error("Company id is not defined");
    }
    setIsLoading(true);
    try {
      await dispatch(
        addGroup({
          companyId,
          groupName,
          selectedGroupManagerIds,
        })
      ).unwrap();
    } catch (error) {
      handleErrorWithToast({
        id: `createGroup-${Date.now().toString()}`,
        title: "Error creating group",
        error,
      });
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  }

  return (
    <>
      {/* Button to open the dialog */}
      <SphereButton onClick={onOpenDialog}>New group</SphereButton>

      {/* Create group dialog */}
      <Dialog
        title="New group"
        open={isDialogOpen}
        confirmText="Create"
        isConfirmDisabled={isCreateDisabled}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        onCancel={() => setIsDialogOpen(false)}
      >
        {/* Group name */}
        <Stack sx={STACK_ERROR_SX}>
          <LabelWithHelp title="Group name" isRequired />
          <FaroSimpleTextField
            placeholder="Enter group name"
            size="small"
            fullWidth
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
          />
          <FaroTextFieldMessage
            shouldHide={!groupNames.includes(groupName)}
            message={{
              type: "info",
              helperText: "Group name already exists",
            }}
          />
        </Stack>

        {/* Groups manager */}
        <Stack sx={STACK_ERROR_SX}>
          <MembersAutocomplete
            options={[...teamOptions, ...memberOptions]}
            handleChange={setSelectedGroupManagerIds}
            placeHolder="Enter name or email"
            message={groupManagersMessage}
            validateNewOption={EmailUtils.isValidEmail}
            // If one of the members is preselected, they should be added to the group by default.
            initialValue={memberOptions.filter(
              (memberOption) => memberOption.isPreselected
            )}
            labelTitle="Group manager"
          />
        </Stack>
      </Dialog>
    </>
  );
}
